"use client";

import React from "react";
import { Loader, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
export default function PartnerWithProspect() {
  const contactInfo = [{
    title: "Management",
    value: "management@glyphicls.com"
  }, {
    title: "Billing",
    value: "billing@glyphicls.com"
  }, {
    title: "Talent",
    value: "talent@glyphicls.com"
  }];
  return <section data-section-id="222638" id="start-a-partnership" className="bg-background-light text-foreground py-20 px-6 md:px-12 lg:px-24 overflow-hidden bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1649121/img/workspace_precision.png)]/5">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-primary leading-[1.1]">
                  Human Precision <br />
                  <span className="italic">at Your Service</span>
                </h2>
                <p className="text-lg text-secondary max-w-md leading-relaxed font-sans">
                  Contact us for specialized medical and legal interpretation led by academic and field experts.
                </p>
              </div>
            </div>

            
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="partnership_form_connect" formId="partnership_form_connect" sectionName="start-a-partnership" className="bg-white/90 p-8 md:p-12 rounded-sm relative" defaultValues={{
            name: "",
            company: "",
            email: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            company: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            }
          }} submitText="Send Message" successMessage="Thank you. We received your message and will respond shortly.">
              <PartnerForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function PartnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-center space-y-8 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-chart-2/10 text-chart-2 rounded-full flex items-center justify-center mb-4 border border-chart-2/20">
          <ShieldCheck className="size-10" />
        </div>
        <div className="space-y-3">
          <h3 className="font-serif text-3xl font-medium text-foreground">Message Sent</h3>
          <p className="text-secondary font-sans max-w-sm mx-auto leading-relaxed">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8">
          New Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1 mb-4">
        <h3 className="font-serif text-2xl font-medium text-primary">Contact Us</h3>
        <p className="text-sm text-muted-foreground font-sans">Complete the fields below to reach our team.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif text-sm text-muted-foreground">Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Vance" {...field} className="bg-background/50 rounded-sm border-border/60 focus-visible:ring-primary/20 h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif text-sm text-muted-foreground">Organization</FormLabel>
              <FormControl>
                <Input placeholder="Healthcare Network S.A." {...field} className="bg-background/50 rounded-sm border-border/60 focus-visible:ring-primary/20 h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif text-sm text-muted-foreground">Professional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="vance@org.com" {...field} className="bg-background/50 rounded-sm border-border/60 focus-visible:ring-primary/20 h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-serif text-sm text-muted-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you today?" className="min-h-[140px] resize-y bg-background/50 rounded-sm border-border/60 focus-visible:ring-primary/20 p-4" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] text-muted-foreground/60">Detail your specific inquiry for our team.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-xs font-mono font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-14 text-sm font-bold rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/10 uppercase tracking-widest">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}